import { apiRequest } from '@/core';
import type { PaginatedResponse, PropertyMediaItem } from './property';

export type RequestStatus =
  | 'submitted'
  | 'replied'
  | 'rejected'
  | 'complete'
  | 'archived';

export type AccommodationRequestUser = {
  id: number;
  email: string;
  display_name: string;
};

export type IndividualRequest = {
  property_id: number;
  season_id?: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  request_details?: string;
  lease_duration: 'year' | 'season';
  persons_count: number;
  accommodation_type: 'line' | 'shared' | 'single';
  extra_services: boolean;
  address?: string;
  property?: PropertyCardData;
};

export type PropertyCardData = {
  id: number;
  name: string;
  dimensions: number;
  bedrooms: number;
  bathrooms: number;
  price: string;
  period: string;
  area?: { id: number; name: string };
  thumbnail?: { fallback_url: string; webp_url: string | null };
};

export type CompanyResponseAssignment = {
  group_id: number;
  price_per_person?: number;
  properties: PropertyCardData[];
};

export type LatestResponse = {
  id: number;
  accommodation_request_id: number;
  responded_by: number;
  assignments: CompanyResponseAssignment[];
  created_at: string;
  updated_at: string;
};

export type AccommodationRequestComment = {
  id: number;
  content: string;
  is_internal: boolean;
  user: AccommodationRequestUser;
  commentable_type: string;
  commentable_id: number;
  parent_id: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

export type CompanyRequestGroup = {
  id: number;
  number_of_employees: number;
  accommodation_type: 'line' | 'shared' | 'single';
  budget_per_person: number;
  distance_km: number;
  bills_included: boolean;
  amenities?: Array<{ amenity_id: number }>;
};

export type CompanyRequest = {
  title: string;
  request_details?: string;
  area_id: number;
  season_id: number;
  extra_services: boolean;
  groups: CompanyRequestGroup[];
};

export type CreateCompanyRequestGroup = Omit<CompanyRequestGroup, 'id'>;

export type CreateCompanyRequest = Omit<CompanyRequest, 'groups'> & {
  groups: CreateCompanyRequestGroup[];
};

export type AccommodationRequest = {
  id: number;
  user_id: number;
  user_type: 'individual' | 'company';
  request_status: RequestStatus;
  created_at: string;
  updated_at: string;
  user: AccommodationRequestUser;
  individual_request?: IndividualRequest | null;
  company_request?: CompanyRequest | null;
  comments?: AccommodationRequestComment[];
  media?: PropertyMediaItem[];
  latest_response?: LatestResponse | null;
};

export type AccommodationResponseAssignmentPayload = {
  group_id: number;
  property_ids: number[];
  price_per_person: number;
};

export type StoreAccommodationResponsePayload = {
  assignments: AccommodationResponseAssignmentPayload[];
  comment?: string;
};

export type ListAccommodationRequestsParams = {
  page?: number;
  per_page?: number;
  sort?: string;
  'filter[request_status]'?: string;
  'filter[user_type]'?: string;
  'filter[user_id]'?: number;
  'filter[created_from]'?: string;
  'filter[created_to]'?: string;
};

export const listComments = (
  requestId: number,
  signal?: AbortSignal,
  includeInternal = false,
) =>
  apiRequest<{ data: AccommodationRequestComment[] }>({
    method: 'GET',
    endpoint: `v1/accommodation_request/${requestId}/comments`,
    params: includeInternal ? { 'filter[is_internal]': true } : undefined,
    signal,
  });

export const postComment = (
  requestId: number,
  content: string,
  isInternal = false,
) =>
  apiRequest<{ data: AccommodationRequestComment }>({
    method: 'POST',
    endpoint: `v1/accommodation_request/${requestId}/comments`,
    body: { content, is_internal: isInternal },
  }).then((response) => response.data);

export const listAccommodationRequests = (
  params?: ListAccommodationRequestsParams,
) =>
  apiRequest<PaginatedResponse<AccommodationRequest>>({
    method: 'GET',
    endpoint: 'v1/accommodation-requests',
    params,
  });

export const listUserAccommodationRequests = (
  params?: ListAccommodationRequestsParams,
) =>
  apiRequest<PaginatedResponse<AccommodationRequest>>({
    method: 'GET',
    endpoint: 'v1/accommodation-requests/user',
    params,
  });

export type CreateAccommodationRequestPayload = {
  user_type: 'individual' | 'company';
  individual_request?: IndividualRequest;
  company_request?: CreateCompanyRequest;
};

export const getAccommodationRequest = (id: number, signal?: AbortSignal) =>
  apiRequest<AccommodationRequest>({
    method: 'GET',
    endpoint: `v1/accommodation-requests/${id}`,
    signal,
  });

export const createAccommodationRequest = (
  data: CreateAccommodationRequestPayload,
) =>
  apiRequest<AccommodationRequest>({
    method: 'POST',
    endpoint: 'v1/accommodation-requests',
    body: data,
  });

export const respondToAccommodationRequest = (
  requestId: number,
  data: StoreAccommodationResponsePayload,
) =>
  apiRequest<LatestResponse>({
    method: 'POST',
    endpoint: `v1/accommodation-requests/${requestId}/respond`,
    body: data,
  });

export const acceptAccommodationRequestResponse = (requestId: number) =>
  apiRequest<AccommodationRequest>({
    method: 'POST',
    endpoint: `v1/accommodation-requests/${requestId}/accept-response`,
  });

export const rejectAccommodationRequestResponse = (
  requestId: number,
  comment: string,
) =>
  apiRequest<AccommodationRequest>({
    method: 'POST',
    endpoint: `v1/accommodation-requests/${requestId}/reject-response`,
    body: { comment },
  });

export const archiveAccommodationRequest = (requestId: number) =>
  apiRequest<AccommodationRequest>({
    method: 'PATCH',
    endpoint: `v1/accommodation-requests/${requestId}/archive`,
  });

export const completeAccommodationRequest = (requestId: number) =>
  apiRequest<AccommodationRequest>({
    method: 'PATCH',
    endpoint: `v1/accommodation-requests/${requestId}/complete`,
  });

type MediaUploadResponse = {
  media?: PropertyMediaItem[];
};

const normalizeUploadedMedia = (
  response: PropertyMediaItem | MediaUploadResponse,
): PropertyMediaItem => {
  if ('uuid' in response && 'url' in response) {
    return response;
  }

  if ('media' in response) {
    const [media] = response.media ?? [];
    if (media) {
      return media;
    }
  }

  throw new Error('Upload completed without returning media.');
};

export const uploadAccommodationRequestAttachment = async (
  requestId: number,
  file: File,
) => {
  const formData = new FormData();
  formData.append('uploads[]', file);
  formData.append('collection_name', 'documents');
  formData.append('name', file.name);

  const response = await apiRequest<PropertyMediaItem | MediaUploadResponse>({
    endpoint: `v1/accommodation_request/${requestId}/media`,
    method: 'POST',
    body: formData,
  });

  return normalizeUploadedMedia(response);
};
